import type { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { useScrollSyncRegister } from "hooks/useScrollSync";
import { GigType } from "model/Gig";
import { ScheduleStatus } from "model/Schedule";
import type {
  GroupByOption,
  ScheduleSection,
  ScheduleShiftItem,
  ScheduleWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";
import {
  formatDateKey,
  formatItemDateKey,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { getAbsenceTypeLabel } from "web-apps/company/utils/absenceLabels";
import { useWorkerAvatar } from "../../hooks/useWorkerAvatar";
import { ShiftBlock } from "../shiftBlock/shiftBlock.component";
import { getScheduleShiftTypeIconName } from "./scheduleRow.typeIcon.logic";
import {
  CellHoverAction,
  MonthAvatar,
  MonthAvatarImage,
  MonthGridRow,
  MonthInfo,
  MonthInfoSpacer,
  MonthName,
  RowInfo,
  NameCellTrigger,
  RowScrollArea,
  RowScrollCard,
  RowSectionMeta,
  RowSectionRole,
  ScheduleRowCell,
  ScheduleRowLayout,
  StickyNameCell,
  VerticalFade,
} from "./scheduleRow.styled";

interface Props {
  readonly days: LocalDate[];
  readonly groupByOption?: GroupByOption;
  readonly shiftBlockSize: "small" | "large" | "pill";
  readonly variant?: "week" | "month";
  readonly workerRow: ScheduleWorkerRow;
  readonly onScheduleClick: (payload: ScheduleCreatePayload) => void;
  readonly onShiftClick: (payload: ShiftClickPayload) => void;
}

const getDateKey = (date: LocalDate): string => formatDateKey(date);

const getItemDateKey = (item: ScheduleShiftItem): string =>
  formatItemDateKey(item.startTime);

export interface ShiftClickPayload {
  item: ScheduleShiftItem;
  worker: ScheduleWorkerRow;
  section: ScheduleSection;
  modalTarget: "shiftDetails" | "teamPublicDetails";
}

export interface ScheduleCreatePayload {
  date: string;
  workerId: number;
  workGroupId: number;
}

const buildItemIndex = (
  items: ScheduleShiftItem[],
): Map<string, ScheduleShiftItem[]> => {
  const index = new Map<string, ScheduleShiftItem[]>();
  items.forEach((item) => {
    const dateKey = getItemDateKey(item);
    const existing = index.get(dateKey);
    if (existing) {
      existing.push(item);
      return;
    }
    index.set(dateKey, [item]);
  });
  return index;
};

const getShiftClickHandler =
  (
    onShiftClick: Props["onShiftClick"],
    item: ScheduleShiftItem,
    workerRow: ScheduleWorkerRow,
    section: ScheduleSection,
  ): (() => void) =>
  () =>
    onShiftClick({
      item,
      worker: workerRow,
      section,
      modalTarget: "shiftDetails",
    });

const getNormalizedKey = (value: string): string =>
  value.trim().toLocaleLowerCase();

export const ScheduleRow: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const isNonWorker = props.workerRow.isWorker === false;
  const resolvedVariant = props.variant ?? "week";
  const cellJustify = "center";
  const cellOffset = 0;
  const monthAvatarBackground = isNonWorker ? ColorV2.Blue6 : ColorV2.White;
  const isMonthView = resolvedVariant === "month";
  const canShowHoverAction = props.workerRow.isWorker === true;
  const registerScrollSync = useScrollSyncRegister();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    const update = (): void => setIsScrolled(element.scrollLeft > 1);
    update();
    element.addEventListener("scroll", update, { passive: true });
    const unregister = registerScrollSync?.(element);
    return (): void => {
      element.removeEventListener("scroll", update);
      unregister?.();
    };
  }, [registerScrollSync]);

  const translatedHeading = isNonWorker
    ? t(`Schedule.${props.workerRow.heading}`, {
        defaultValue: props.workerRow.heading,
      })
    : props.workerRow.heading;
  const nonWorkerRoleHeading =
    props.workerRow.text2?.trim() ?? props.workerRow.text?.trim() ?? "";
  const resolvedHeading =
    isNonWorker && nonWorkerRoleHeading.length > 0
      ? nonWorkerRoleHeading
      : translatedHeading;

  const translatedText2 = props.workerRow.text2
    ? t(`Schedule.${props.workerRow.text2}`, {
        defaultValue: props.workerRow.text2,
      })
    : undefined;

  const avatarUrl = useWorkerAvatar({
    workerExternalId: props.workerRow.externalId,
    isWorker: props.workerRow.isWorker,
  });

  const dayKeys = useMemo(() => props.days.map(getDateKey), [props.days]);
  const sectionItemIndexes = useMemo(
    () =>
      props.workerRow.sections.map((section) => buildItemIndex(section.items)),
    [props.workerRow.sections],
  );

  const monthTooltipContent = useMemo((): React.ReactNode => {
    if (!isMonthView) return null;
    let teamsLine = "";
    if (props.groupByOption !== "team") {
      const teamsByNormalizedName = new Map<string, string>();
      props.workerRow.sections.forEach((section) => {
        const teamName = section.text?.trim() ?? "";
        if (teamName.length === 0) return;
        const normalizedTeamName = getNormalizedKey(teamName);
        if (!teamsByNormalizedName.has(normalizedTeamName)) {
          teamsByNormalizedName.set(normalizedTeamName, teamName);
        }
      });
      teamsLine = Array.from(teamsByNormalizedName.values()).join(", ");
    }
    const roleLine = props.workerRow.text?.trim() ?? "";
    if (
      teamsLine &&
      roleLine &&
      teamsLine.localeCompare(roleLine, undefined, {
        sensitivity: "accent",
      }) === 0
    ) {
      return teamsLine;
    }
    if (teamsLine && roleLine) {
      return (
        <>
          <span>{teamsLine}</span>
          <span>{roleLine}</span>
        </>
      );
    }
    return teamsLine || roleLine || null;
  }, [
    isMonthView,
    props.groupByOption,
    props.workerRow.sections,
    props.workerRow.text,
  ]);

  const renderedRows = props.workerRow.sections.flatMap(
    (section, sectionIndex) => {
      const itemIndex = sectionItemIndexes[sectionIndex];
      const getItemsForKey = (dateKey: string): ScheduleShiftItem[] =>
        itemIndex.get(dateKey) ?? [];
      const rowCount = Math.max(
        1,
        ...dayKeys.map((dateKey) => getItemsForKey(dateKey).length),
      );
      return Array.from({ length: rowCount }, (_, rowIndex) => {
        const rowKey = `${props.workerRow.id}-${section.id}-${rowIndex}`;
        const firstItemInRow = dayKeys
          .map((dateKey) => getItemsForKey(dateKey)[rowIndex])
          .find((item): item is ScheduleShiftItem => Boolean(item));
        const nonWorkerIconName =
          firstItemInRow?.gigType === GigType.WORKPOOL ||
          firstItemInRow?.gigType === GigType.WORKGROUP
            ? "v2/team"
            : "v2/globe";
        const canOpenFromNameCell =
          isNonWorker && Boolean(firstItemInRow) && rowIndex === 0;
        const handleNameCellClick = (): void => {
          if (!firstItemInRow) return;
          props.onShiftClick({
            item: firstItemInRow,
            worker: props.workerRow,
            section,
            modalTarget: "teamPublicDetails",
          });
        };
        const monthInfoElement = (
          <MonthInfo>
            <MonthAvatar $backgroundColor={monthAvatarBackground}>
              {avatarUrl ? (
                <MonthAvatarImage
                  src={avatarUrl}
                  alt={props.workerRow.heading}
                />
              ) : (
                <CustomIcon
                  name={isNonWorker ? nonWorkerIconName : "v2/person"}
                  size="18px"
                  color={ColorV2.Blue3}
                />
              )}
            </MonthAvatar>
            <MonthName title={translatedHeading}>{resolvedHeading}</MonthName>
          </MonthInfo>
        );
        const monthInfoContent = monthTooltipContent ? (
          <TooltipHover
            content={monthTooltipContent}
            arrow="down"
            textAlign="left"
            minWidth={140}
            maxWidth={260}
            offset={8}
          >
            {monthInfoElement}
          </TooltipHover>
        ) : (
          monthInfoElement
        );
        const gridChildren = (
          <>
            <StickyNameCell $isSticky={isMonthView}>
              {rowIndex > 0 ? (
                isMonthView ? (
                  <MonthInfoSpacer />
                ) : (
                  <RowSectionMeta>
                    {props.workerRow.text && (
                      <RowSectionRole>{props.workerRow.text}</RowSectionRole>
                    )}
                  </RowSectionMeta>
                )
              ) : sectionIndex === 0 ? (
                <RowInfo>
                  <NameCellTrigger
                    $isInteractive={canOpenFromNameCell}
                    onClick={
                      canOpenFromNameCell ? handleNameCellClick : undefined
                    }
                  >
                    {isMonthView ? (
                      monthInfoContent
                    ) : (
                      <AvatarChip
                        title={resolvedHeading}
                        subTitle={
                          isNonWorker
                            ? translatedText2
                            : (props.workerRow.text ?? null)
                        }
                        avatarUrl={isNonWorker ? null : avatarUrl}
                        iconName={isNonWorker ? nonWorkerIconName : undefined}
                        avatarBackgroundColor={
                          isNonWorker ? ColorV2.Blue6 : undefined
                        }
                      />
                    )}
                  </NameCellTrigger>
                </RowInfo>
              ) : (
                <MonthInfoSpacer />
              )}
              {isScrolled && <VerticalFade />}
            </StickyNameCell>
            {dayKeys.map((dateKey) => {
              const item = getItemsForKey(dateKey)[rowIndex];
              const isPublicOrTeamGig =
                item?.gigType === GigType.PUBLIC ||
                item?.gigType === GigType.WORKPOOL ||
                item?.gigType === GigType.WORKGROUP;
              const workerName =
                item?.workerId != null && item.workerName
                  ? item.workerName
                  : null;
              const hoverWorkerName =
                isPublicOrTeamGig && workerName ? workerName : null;
              const statusLabelOverrides =
                item &&
                (item.status === ScheduleStatus.PLANNED_ABSENCE ||
                  item.status === ScheduleStatus.UNPLANNED_ABSENCE)
                  ? {
                      [item.status]:
                        item.details?.[0]?.resolution != null
                          ? getAbsenceTypeLabel(item.details[0].resolution, t)
                          : t("Schedule.Absence"),
                    }
                  : undefined;
              const typeIconName =
                item != null && !isNonWorker
                  ? getScheduleShiftTypeIconName(item.gigType)
                  : undefined;
              const shiftBlockElement = item ? (
                <ShiftBlock
                  startTime={item.startTime}
                  endTime={item.endTime}
                  status={item.status}
                  size={props.shiftBlockSize}
                  workerName={hoverWorkerName}
                  statusLabelOverrides={statusLabelOverrides}
                  typeIconName={typeIconName}
                  onClick={getShiftClickHandler(
                    props.onShiftClick,
                    item,
                    props.workerRow,
                    section,
                  )}
                />
              ) : null;
              return (
                <ScheduleRowCell
                  key={`${section.id}-${dateKey}-${rowIndex}`}
                  $justify={cellJustify}
                  $offset={cellOffset}
                >
                  {shiftBlockElement}
                  {!item && canShowHoverAction && rowIndex === 0 && (
                    <CellHoverAction
                      type="button"
                      $size={props.shiftBlockSize}
                      onClick={(event) => {
                        event.stopPropagation();
                        props.onScheduleClick({
                          date: dateKey,
                          workerId: props.workerRow.id,
                          workGroupId: section.id,
                        });
                      }}
                    >
                      {props.shiftBlockSize === "pill"
                        ? "+"
                        : `+ ${t("Schedule.Schedule")}`}
                    </CellHoverAction>
                  )}
                </ScheduleRowCell>
              );
            })}
          </>
        );
        return isMonthView ? (
          <MonthGridRow key={rowKey} $dayCount={props.days.length}>
            {gridChildren}
          </MonthGridRow>
        ) : (
          <ScheduleRowLayout key={rowKey} $dayCount={props.days.length}>
            {gridChildren}
          </ScheduleRowLayout>
        );
      });
    },
  );

  return (
    <RowScrollCard>
      <RowScrollArea ref={scrollRef}>{renderedRows}</RowScrollArea>
    </RowScrollCard>
  );
};
